import { onMounted, onUnmounted, Ref, ref } from "vue";
import {
  HubConnectionBuilder,
  HubConnection,
  HttpTransportType,
  LogLevel,
} from "@microsoft/signalr";
import { TicketPurchaseDtoViewModel } from "@/viewmodels.g";

/**
 * Composable for managing real-time ticket availability updates via SignalR
 * Subscribes to seat/ticket availability changes for a specific event date
 * and updates the available tickets for each price in real-time
 */
export function useTicketAvailability(
  eventDateId: Ref<number | undefined>,
  eventPrices: Ref<
    Array<{
      id: number;
      rootId: number;
      availableTickets: number | null;
      isSoldOut?: boolean;
      seatMapSectionId?: number | null;
    }>
  >,
  purchase: Ref<TicketPurchaseDtoViewModel>,
) {
  const connection: Ref<HubConnection | null> = ref(null);
  const isConnected = ref(false);
  // Store availableSeatsBySection in a separate reactive ref for better Vue reactivity tracking
  const availableSeatsBySectionRef = ref<{ [key: string]: number } | null>(
    null,
  );

  const startConnection = async () => {
    if (!eventDateId.value) {
      return;
    }

    if (connection.value) {
      // Already connected
      return;
    }

    try {
      connection.value = new HubConnectionBuilder()
        .withUrl("/hubs/seating", {
          // Configured to avoid issues with load balancing:
          // https://docs.microsoft.com/en-us/aspnet/core/signalr/scale?view=aspnetcore-6.0#sticky-sessions
          skipNegotiation: true,
          transport: HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Information)
        .build();

      // Set up event handler before starting connection
      connection.value.on(
        "SeatAvailabilityChanged",
        (data: {
          eventDateId: number;
          availableSeats?: string[];
          availableSeatDetail?: Array<{
            seatNumber: string;
            amountOfSeats: number;
          }>;
          availableTicketsByPrice?: { [key: string]: number | null };
          totalAmountOfAvailableSeats?: number;
          availableSeatsBySection?: { [key: string]: number };
        }) => {
          if (data.eventDateId === eventDateId.value) {
            // Update section-specific availability if provided
            if (
              data.availableSeatsBySection &&
              (purchase.value.eventDate as any)
            ) {
              // Create new object reference for reactivity
              const newValue = { ...data.availableSeatsBySection };
              // Update reactive ref (Vue will track this)
              availableSeatsBySectionRef.value = newValue;
              // Also update nested property for backward compatibility
              (purchase.value.eventDate as any).availableSeatsBySection =
                newValue;

              // Update isSoldOut for GA section prices based on section-specific availability
              eventPrices.value.forEach((price) => {
                const sectionId = (price as any).seatMapSectionId;
                if (sectionId && data.availableSeatsBySection) {
                  const sectionAvailable =
                    data.availableSeatsBySection[sectionId.toString()];
                  if (
                    sectionAvailable !== undefined &&
                    sectionAvailable !== null
                  ) {
                    // For GA sections, update isSoldOut based on section-specific availability
                    // Only update if this price doesn't have its own MaxQuantity (relies on section capacity)
                    if (
                      price.availableTickets === null ||
                      price.availableTickets === undefined
                    ) {
                      price.isSoldOut = sectionAvailable <= 0;
                    }
                  }
                }
              });
            }

            // Update available tickets for each price (for events with MaxQuantity per price)
            if (data.availableTicketsByPrice) {
              eventPrices.value.forEach((price) => {
                const rootId = price.rootId.toString();
                if (rootId in data.availableTicketsByPrice!) {
                  price.availableTickets =
                    data.availableTicketsByPrice![rootId];
                  // Update isSoldOut flag when availableTickets reaches 0
                  price.isSoldOut = (price.availableTickets ?? 0) <= 0;
                }
              });
            }

            // Update overall available seats (for general admission events without per-price limits)
            if (
              data.totalAmountOfAvailableSeats !== undefined &&
              purchase.value.eventDate
            ) {
              purchase.value.eventDate.availableSeats =
                data.totalAmountOfAvailableSeats;

              // For events without per-price limits, update isSoldOut on all prices when overall capacity reaches 0
              if (data.totalAmountOfAvailableSeats <= 0) {
                eventPrices.value.forEach((price) => {
                  // Only update prices that don't have their own MaxQuantity (rely on overall capacity)
                  if (
                    price.availableTickets === null ||
                    price.availableTickets === undefined
                  ) {
                    price.isSoldOut = true;
                  }
                });
              }
            }
          }
        },
      );

      await connection.value.start();
      isConnected.value = true;

      // Subscribe to updates for this specific event date
      await connection.value.send("SubscribeToSeating", eventDateId.value);
    } catch (error) {
      console.error("Error establishing SignalR connection:", error);
      isConnected.value = false;
    }
  };

  const stopConnection = async () => {
    if (connection.value) {
      try {
        await connection.value.stop();
      } catch (error) {
        console.error("Error stopping SignalR connection:", error);
      } finally {
        connection.value = null;
        isConnected.value = false;
      }
    }
  };

  onMounted(() => {
    startConnection();
  });

  onUnmounted(() => {
    stopConnection();
  });

  return {
    isConnected,
    startConnection,
    stopConnection,
    availableSeatsBySection: availableSeatsBySectionRef,
  };
}
